/**
 * ExplorationContext - React context for passing exploration state to ObjectNode components.
 *
 * ReactFlow node components receive data via the `data` prop, but complex callbacks
 * and shared state are better passed via context to avoid serialization issues
 * and unnecessary re-renders.
 */

import { createContext, type Context } from 'react';

import type { ObjectNodeData } from '../nodes/ObjectNode';

export type ExplorationContextType = {
	/** Enter focus mode on a node (triggered by double-click) */
	onFocus: (nodeData: ObjectNodeData) => void;
	/** Exit focus mode */
	onExitFocus: () => void;
	/** Navigate back to the focused node from an active exploration node and close the Explorer panel */
	onGoBackToFocused: () => void;
	/** The ARI of the currently focused node (for styling) */
	focusedNodeAri: string | null;
	/** The ARI of the currently active exploration node (for bold text styling) */
	activeExplorationNodeAri: string | null;
	/** Set of node ARIs that have exploration data (for purple dot indicator) */
	exploredNodeAris: Set<string>;
	/** Set of node IDs that have incoming edges (target of an edge) */
	nodesWithIncomingEdges: Set<string>;
	/** Set of node IDs that have outgoing edges (source of an edge) */
	nodesWithOutgoingEdges: Set<string>;
};

export const ExplorationContext: Context<ExplorationContextType> =
	createContext<ExplorationContextType>({
		onFocus: () => {},
		onExitFocus: () => {},
		onGoBackToFocused: () => {},
		focusedNodeAri: null,
		activeExplorationNodeAri: null,
		exploredNodeAris: new Set(),
		nodesWithIncomingEdges: new Set(),
		nodesWithOutgoingEdges: new Set(),
	});
